// Browser ⇄ hub transports: WebTransport (one bidi stream, u32 BE length-prefixed frames)
// and WebSocket (one binary message per frame), plus the connect race between them.

export type TransportKind = 'wt' | 'ws';

export interface Transport {
  readonly kind: TransportKind;
  /** Send one frame. Never blocks; frames are queued by the underlying transport. */
  send(frame: Uint8Array): void;
  close(): void;
  /** Set by the owner before any frame is delivered. */
  onFrame: (frame: Uint8Array) => void;
  onClose: (reason: string) => void;
}

const noop = () => {};

// ---------------------------------------------------------------------------
// WebSocket

class WsTransport implements Transport {
  readonly kind = 'ws' as const;
  onFrame: (f: Uint8Array) => void = noop;
  onClose: (r: string) => void = noop;
  private closed = false;

  constructor(private ws: WebSocket) {
    ws.onmessage = (ev) => {
      if (ev.data instanceof ArrayBuffer) this.onFrame(new Uint8Array(ev.data));
    };
    ws.onclose = (ev) => this.finish(`ws closed (${ev.code}${ev.reason ? ' ' + ev.reason : ''})`);
    ws.onerror = () => this.finish('ws error');
  }

  send(frame: Uint8Array): void {
    if (this.closed || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(frame as Uint8Array<ArrayBuffer>);
  }

  close(): void {
    try {
      this.ws.close();
    } catch {
      /* ignore */
    }
    this.finish('closed by client');
  }

  private finish(reason: string) {
    if (this.closed) return;
    this.closed = true;
    this.ws.onmessage = this.ws.onclose = this.ws.onerror = null;
    this.onClose(reason);
  }
}

function connectWs(url: string, signal: AbortSignal): Promise<Transport> {
  return new Promise((resolve, reject) => {
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      reject(e);
      return;
    }
    ws.binaryType = 'arraybuffer';
    const cleanup = () => {
      ws.onopen = ws.onerror = ws.onclose = null;
      signal.removeEventListener('abort', onAbort);
    };
    const onAbort = () => {
      cleanup();
      try {
        ws.close();
      } catch {
        /* ignore */
      }
      reject(new Error('aborted'));
    };
    signal.addEventListener('abort', onAbort);
    ws.onopen = () => {
      cleanup();
      resolve(new WsTransport(ws));
    };
    ws.onerror = ws.onclose = () => {
      cleanup();
      reject(new Error('websocket failed'));
    };
  });
}

// ---------------------------------------------------------------------------
// WebTransport

class WtTransport implements Transport {
  readonly kind = 'wt' as const;
  onFrame: (f: Uint8Array) => void = noop;
  onClose: (r: string) => void = noop;
  private closed = false;
  private pending: Uint8Array | null = null;

  constructor(
    private wt: WebTransport,
    private writer: WritableStreamDefaultWriter<Uint8Array>,
    private reader: ReadableStreamDefaultReader<Uint8Array>,
  ) {
    wt.closed.then(
      () => this.finish('wt closed'),
      (e) => this.finish(`wt closed: ${String(e)}`),
    );
    void this.readLoop();
  }

  send(frame: Uint8Array): void {
    if (this.closed) return;
    const buf = new Uint8Array(4 + frame.length);
    new DataView(buf.buffer).setUint32(0, frame.length);
    buf.set(frame, 4);
    this.writer.write(buf).catch(() => this.finish('wt write failed'));
  }

  close(): void {
    try {
      this.wt.close();
    } catch {
      /* ignore */
    }
    this.finish('closed by client');
  }

  private async readLoop() {
    try {
      for (;;) {
        const { value, done } = await this.reader.read();
        if (done || this.closed) break;
        if (value && value.length) this.feed(value);
      }
      this.finish('wt stream ended');
    } catch (e) {
      this.finish(`wt read failed: ${String(e)}`);
    }
  }

  /** Split a chunk into frames; complete frames inside a chunk are delivered as views (no copy). */
  private feed(chunk: Uint8Array) {
    let buf = chunk;
    if (this.pending) {
      const merged = new Uint8Array(this.pending.length + chunk.length);
      merged.set(this.pending, 0);
      merged.set(chunk, this.pending.length);
      buf = merged;
      this.pending = null;
    }
    const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
    let pos = 0;
    while (buf.length - pos >= 4) {
      const len = dv.getUint32(pos);
      if (buf.length - pos - 4 < len) break;
      const f = buf.subarray(pos + 4, pos + 4 + len);
      pos += 4 + len;
      if (this.closed) return;
      this.onFrame(f);
    }
    if (pos < buf.length) this.pending = buf.slice(pos);
  }

  private finish(reason: string) {
    if (this.closed) return;
    this.closed = true;
    try {
      this.reader.cancel().catch(noop);
    } catch {
      /* ignore */
    }
    this.onClose(reason);
  }
}

async function connectWt(url: string, signal: AbortSignal): Promise<Transport> {
  const wt = new WebTransport(url);
  wt.closed.catch(() => {}); // observed by WtTransport once connected; avoid unhandled rejections
  const onAbort = () => {
    try {
      wt.close();
    } catch {
      /* ignore */
    }
  };
  signal.addEventListener('abort', onAbort);
  try {
    await wt.ready;
    const stream = await wt.createBidirectionalStream();
    if (signal.aborted) throw new Error('aborted');
    const writer = stream.writable.getWriter() as WritableStreamDefaultWriter<Uint8Array>;
    const reader = stream.readable.getReader() as ReadableStreamDefaultReader<Uint8Array>;
    return new WtTransport(wt, writer, reader);
  } catch (e) {
    onAbort();
    throw e;
  } finally {
    signal.removeEventListener('abort', onAbort);
  }
}

export function webTransportSupported(): boolean {
  return typeof (globalThis as { WebTransport?: unknown }).WebTransport === 'function';
}

// ---------------------------------------------------------------------------
// Race

export interface RaceOptions {
  wt?: string | null;
  ws: string;
  /** Head start given to WebTransport before WebSocket is attempted. */
  headStartMs?: number;
  timeoutMs?: number;
}

/**
 * Race WebTransport (with a head start) against WebSocket. Resolves with the first transport
 * whose handshake completes; the loser is closed.
 */
export function raceConnect(opts: RaceOptions): Promise<Transport> {
  const headStart = opts.headStartMs ?? 300;
  const timeoutMs = opts.timeoutMs ?? 8000;
  const tryWt = !!opts.wt && webTransportSupported();

  return new Promise((resolve, reject) => {
    let settled = false;
    let failures = 0;
    const attempts = tryWt ? 2 : 1;
    const controllers: AbortController[] = [];
    let wsTimer: ReturnType<typeof setTimeout> | undefined;
    const errors: string[] = [];

    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      controllers.forEach((c) => c.abort());
      clearTimeout(wsTimer);
      reject(new Error('connect timeout'));
    }, timeoutMs);

    const win = (t: Transport) => {
      if (settled) {
        t.close();
        return;
      }
      settled = true;
      clearTimeout(timeout);
      clearTimeout(wsTimer);
      controllers.forEach((c) => c.abort());
      resolve(t);
    };
    const lose = (kind: string, e: unknown) => {
      errors.push(`${kind}: ${e instanceof Error ? e.message : String(e)}`);
      failures++;
      if (kind === 'wt' && wsTimer !== undefined && !wsStarted) {
        clearTimeout(wsTimer);
        startWs();
      }
      if (failures >= attempts && !settled) {
        settled = true;
        clearTimeout(timeout);
        reject(new Error(errors.join('; ')));
      }
    };

    let wsStarted = false;
    const startWs = () => {
      if (wsStarted || settled) return;
      wsStarted = true;
      const c = new AbortController();
      controllers.push(c);
      connectWs(opts.ws, c.signal).then(win, (e) => lose('ws', e));
    };

    if (tryWt) {
      const c = new AbortController();
      controllers.push(c);
      connectWt(opts.wt!, c.signal).then(win, (e) => lose('wt', e));
      wsTimer = setTimeout(startWs, headStart);
    } else {
      startWs();
    }
  });
}
